import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import React, { useState, useMemo, useEffect } from "react"
import { Card, CardBody, Table, Input, Button } from "reactstrap"
import { FaEdit, FaEye, FaPlus, FaFileAlt, FaUpload } from "react-icons/fa"
import { MdDelete } from "react-icons/md"
import Swal from "sweetalert2"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import ProjectModal from "./ProjectModal"
import secureLocalStorage from "react-secure-storage"
import { useNavigate } from "react-router-dom"
import Configs from "../../config"
import { usePermissionData } from "AppContext"
import PermittedComponent from "PermittedComponent"

const ProjectIndex = () => {
  const { checkAccess } = usePermissionData()

  document.title = "Departments | Siddhi Tax Consultant"

  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [projectData, setProjectData] = useState([])
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  const [staff, setStaff] = useState([])

  // Modal states
  const [modalOpen, setModalOpen] = useState(false)
  const [docModalOpen, setDocModalOpen] = useState(false)
  const [selectedProject, setSelectedProject] = useState({})
  const [viewOnly, setViewOnly] = useState(false)

  useEffect(() => {
    getProjectDetails()
    getStaffDetails()
  }, [])

  async function getStaffDetails() {
    const APIdata = await fetchData(setLoading, "users")
    if (APIdata?.success) {
      setStaff(APIdata?.data?.reverse())
    }
  }

  async function getProjectDetails() {
    const APIdata = await fetchData(setLoading, "projects")

    if (APIdata?.success) {
      const Data = APIdata.data.map(item => ({
        ...item,
        permissions: item?.permissions
          ? typeof item.permissions === "string"
            ? JSON.parse(item.permissions)
            : item.permissions
          : [],
      }))

      setProjectData(Data.reverse())
    }
  }

  const filteredData = useMemo(() => {
    return projectData.filter(
      item =>
        item?.projectName?.toLowerCase().includes(searchString.toLowerCase()) ||
        item?.location?.toLowerCase().includes(searchString.toLowerCase())
    )
  }, [projectData, searchString])

  const totalPages = Math.ceil(filteredData.length / pageSize)
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(() => {
    return filteredData.slice(start, start + pageSize)
  }, [filteredData, currentPage, pageSize])

  const handleDelete = (project, id) => {
    Swal.fire({
      title: `Are you sure you want to delete "${project?.projectName}"?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchDeleteData(setLoading, "projects", id)
        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Department Deleted Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          getProjectDetails()
        } else {
          Swal.fire({
            title: "Error Deleting Department.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  const handleSubmit = async () => {
    const payload = {
      ...selectedProject,
      assignes: (selectedProject?.permissions || []).map(u => u.userId),
      permissions: JSON.stringify(selectedProject?.permissions || []),
    }

    if (selectedProject?.id) {
      const APIdata = await FetchPutData(
        setLoading,
        "projects",
        selectedProject?.id,
        payload
      )
      if (APIdata?.data?.success) {
        Swal.fire({
          title: "Department Updated Successfully",
          text: APIdata?.data?.message,
          icon: "success",
        })
        getProjectDetails()
        setModalOpen(false)
      } else {
        Swal.fire({
          title: "Error Updating Department",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    } else {
      const APIdata = await FetchPostData(setLoading, "projects", payload)
      if (APIdata?.data?.success) {
        Swal.fire({
          title: "Department Added Successfully",
          text: APIdata?.data?.message,
          icon: "success",
        })
        getProjectDetails()
        setModalOpen(false)
      } else {
        Swal.fire({
          title: "Error Updating Department",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    }
  }

  const handleStatusUpdate = async (project, status) => {
    Swal.fire({
      title: `Are you sure you want to update Status "${project?.projectName}"?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Update it!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchPutData(
          setLoading,
          "projects",
          project?.id,
          {
            status,
          }
        )
        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Project Status Updated Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          getProjectDetails()
        } else {
          Swal.fire({
            title: "Error Updating Status.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  return (
    // <PermittedComponent
    //   isAccess={checkAccess(2, "view") || checkAccess(2, "viewglobal")}
    // >
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Dashboard" breadcrumbItem="Department" />

      <Card className="shadow-lg">
        <CardBody>
          {/* Top Controls */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex gap-3">
              <Input
                type="select"
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value))
                  setCurrentPage(1)
                }}
                className="rounded-4 w-auto pe-5"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>

              <Input
                className="rounded-4"
                placeholder="Search by name, location or assigned person..."
                value={searchString}
                onChange={e => {
                  setSearchString(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>

            {/* {checkAccess(2, "add") && ( */}
            <Button
              className="btn-blue rounded-4 px-3"
              onClick={() => {
                setSelectedProject({})
                setViewOnly(false)
                setModalOpen(true)
              }}
            >
              <FaPlus className="me-2" />
              Add Department
            </Button>
            {/* )} */}
          </div>

          {/* Table */}
          <Table bordered responsive>
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>Department</th>
                <th>Assigned Person</th>
                {/* <th>Status</th> */}
                <th className="text-center">Action</th>{" "}
                <th className="text-center">Documents</th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-3">
                    No Departments found
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{start + index + 1}</td>
                    <td>{item?.projectName || "-"}</td>
                    <td>
                      {item?.users?.length > 0 ? (
                        <div className="d-flex gap-1 flex-wrap">
                          {item.users.map((user, index) => (
                            <span
                              key={index}
                              className="badge bg-blue fs-6 px-2 py-1"
                            >
                              {user?.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    {/* <td>
                        {checkAccess(2, "edit") && (
                          <select
                            className="form-select w-auto"
                            value={item?.status}
                            onChange={e =>
                              handleStatusUpdate(item, e.target.value)
                            }
                          >
                            <option value="">Not Started</option>
                            <option value="1">In-Progess</option>
                            <option value="2">Completed</option>
                            <option value="3">Canceled</option>
                            <option value="4">On Hold</option>
                          </select>
                        )}
                      </td> */}
                    <td className="text-center d-flex">
                      {/* View */}
                      {/* {(checkAccess(2, "view") ||
                          checkAccess(2, "viewglobal")) && ( */}
                      <Button
                        title="View"
                        className="bg-secondary text-white me-2 border-0 shadow-none"
                        onClick={() => {
                          setSelectedProject(item)
                          setViewOnly(true)
                          setModalOpen(true)
                        }}
                      >
                        <FaEye size={16} />
                      </Button>
                      {/* )} */}
                      {/* {checkAccess(2, "edit") && ( */}
                      <Button
                        title="Edit"
                        className="bg-success text-white me-2 border-0 shadow-none"
                        onClick={() => {
                          setSelectedProject(item)
                          setViewOnly(false)
                          setModalOpen(true)
                        }}
                      >
                        <FaEdit size={16} />
                      </Button>
                      {/* )} */}

                      {/* Delete */}
                      {/* {checkAccess(2, "delete") && ( */}
                      <Button
                        title="Delete"
                        className="bg-danger text-white border-0 shadow-none"
                        onClick={() => handleDelete(item, item?.id)}
                      >
                        <MdDelete size={18} />
                      </Button>
                      {/* )} */}
                    </td>
                    <td className="text-center">
                      {/* Documents */}

                      <Button
                        title="View"
                        className="bg-warning text-white me-2 border-0 shadow-none"
                        onClick={() => {
                          navigate(
                            `${Configs.appurl}projects/documents/view/${item.id}`
                          )
                        }}
                      >
                        <FaFileAlt size={16} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <nav className="d-flex justify-content-between align-items-center mt-3">
            <h5 className="mb-0">
              Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {projectData.length} entries
            </h5>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages || totalPages === 0
                    ? "disabled"
                    : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>

      {/* Project Add/Edit/View Modal — plug in your ProjectModal here */}
      <ProjectModal
        open={modalOpen}
        toggle={() => setModalOpen(false)}
        selectedProject={selectedProject}
        setSelectedProject={setSelectedProject}
        viewOnly={viewOnly}
        onSubmit={handleSubmit}
        staffList={staff}
      />
    </div>
    // </PermittedComponent>
  )
}

export default ProjectIndex
